import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { ButtonComponent } from "@odx/angular/components/button";
import { ModalRef } from "@odx/angular/components/modal";

export type MultiSelectGroupModalResult = { group: ProductItemGroup };

interface MultiSelectGroupModalData {
  groups: ProductItemGroup[];
  hasAlreadyGroupedItems: boolean;
  floorplanId: string;
  nextGroupId: number;
}

@Component({
  selector: "app-multi-select-group-modal",
  imports: [UiKitModule, AreaHeaderComponent, ButtonComponent, ReactiveFormsModule],
  templateUrl: "./multi-select-group-modal.component.html",
  styleUrl: "./multi-select-group-modal.component.scss",
})
export class MultiSelectGroupModalComponent implements OnInit {
  public readonly modalRef = inject(ModalRef<MultiSelectGroupModalData, MultiSelectGroupModalResult>);
  protected form!: FormGroup;
  protected groups: ProductItemGroup[] = [];
  protected hasAlreadyGroupedItems = false;
  protected groupMode: "existing" | "new" = "existing";
  private formBuilder = inject(FormBuilder);
  private floorplanId!: string;
  private nextGroupId!: number;

  ngOnInit(): void {
    this.groups = this.modalRef.data.groups;
    this.hasAlreadyGroupedItems = this.modalRef.data.hasAlreadyGroupedItems;
    this.floorplanId = this.modalRef.data.floorplanId;
    this.nextGroupId = this.modalRef.data.nextGroupId;

    // If no groups, preselect "new" mode
    const initialMode = this.groups.length === 0 ? "new" : "existing";

    this.form = this.formBuilder.group({
      groupMode: [initialMode],
      selectedGroupId: [null],
      newGroupName: [null, Validators.minLength(1)],
    });

    this.groupMode = initialMode;

    // Set initial mode (enable/disable controls)
    this.setGroupMode(initialMode);

    // Listen to groupMode changes
    this.form.get("groupMode")?.valueChanges.subscribe((mode) => {
      this.setGroupMode(mode);
    });
  }

  protected setGroupMode(mode: "existing" | "new"): void {
    // Don't allow switching to "existing" if no groups available
    if (mode === "existing" && this.groups.length === 0) {
      this.form.patchValue({ groupMode: "new" });
      return;
    }

    this.groupMode = mode;
    if (mode === "existing") {
      this.form.get("selectedGroupId")?.enable();
      this.form.get("newGroupName")?.disable();
    } else {
      this.form.get("selectedGroupId")?.disable();
      this.form.get("newGroupName")?.enable();
    }
  }

  protected get isConfirmDisabled(): boolean {
    const groupMode = this.form.value.groupMode || this.groupMode;
    const rawValue = this.form.getRawValue();
    if (groupMode === "existing") {
      return !rawValue.selectedGroupId;
    } else {
      return !rawValue.newGroupName?.trim();
    }
  }

  protected onConfirm() {
    let group: ProductItemGroup;
    const formValue = this.form.value;
    const rawValue = this.form.getRawValue();
    const groupMode = formValue.groupMode || this.groupMode;

    if (groupMode === "new") {
      const newGroupName = rawValue.newGroupName?.trim();
      group = new ProductItemGroup(newGroupName, this.nextGroupId, [this.floorplanId]);
    } else {
      const selectedGroupId = formValue.selectedGroupId;
      group = this.groups.find((g) => g.id === selectedGroupId)!;
    }

    this.modalRef.close({ group });
  }
}
